import client from "services/ngage/api-client";
import {Tag, TagData} from "types/Tag";
import deserializeTag from "services/ngage/deserializers/tag";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

type TagFilters = {
    search?: string | null
    page?: number
    perPage?: number
}

/**
 * Get all tags that match the given filters (paginated)
 */
const filtered = async (filters: TagFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Tag> | null>('/tags', {params})).data

        if (page) page.data = page.data.map(deserializeTag)

        return page
    } catch (error) {
        console.error("Unable to fetch news tags", error)
        return null
    }
}

/**
 * Get a single tag
 *
 * @param {number} tagId
 */
const get = async (tagId: number) => {
    try {
        const tag = (await client.get<Tag | null>(`/tags/${tagId}`)).data
        return tag ? deserializeTag(tag) : null
    } catch (error) {
        console.error(`Unable to fetch tag ${tagId}`, error)
        return null
    }
}

/**
 * Store a new tag
 *
 * @param data
 */
const store = async (data: TagData) => {
    try {
        const tag = (await client.post<Tag | null>('/tags', data)).data
        return tag ? deserializeTag(tag) : null
    } catch (error) {
        console.error("Unable to store tag", error)
        throw error
    }
}

/**
 * Update an existing tag
 *
 * @param {number} tagId
 * @param data
 */
const update = async (tagId: number, data: TagData) => {
    try {
        const tag = (await client.put<Tag | null>(`/tags/${tagId}`, data)).data
        return tag ? deserializeTag(tag) : null
    } catch (error) {
        console.error(`Unable to update tag ${tagId}`, error)
        throw error
    }
}

/**
 * Permanently delete a tag
 *
 * @param {number} tagId
 */
const destroy = async (tagId: number) => {
    try {
        return (await client.delete<null>(`/tags/${tagId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete tag ${tagId}`, error)
        throw error
    }
}

const tags = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default tags;